import { useState, useEffect, useRef } from "react";
import "./css/productadd.css";
import { apiPostFormData, apiGet } from "../../../utils/apiClient";

const ProductAdd = ({ onClose }) => {
  const [formData, setFormData] = useState({
    categoryIdx: "",
    productName: "",
    productPrice: "",
    isAdult: false,
    isStock: false,
    productAmount: "",
    productImgs: [],
  });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [descImgFiles, setDescImgFiles] = useState([]);
  const descEditorRef = useRef(null);
  const descImgInputRef = useRef(null);
  const savedRangeRef = useRef(null);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await apiGet("/category");
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setCategories(data);
        }
      } else {
        console.error("카테고리 조회 실패");
      }
    } catch (err) {
      console.error("카테고리 조회 실패:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // 상품가격과 재고량은 10자리로 제한
    if (
      (name === "productPrice" || name === "productAmount") &&
      value.length > 10
    ) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    // 파일 크기 체크 (10MB 제한)
    const maxSize = 10 * 1024 * 1024;
    const oversizedFiles = files.filter((file) => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      alert("이미지 파일 크기는 10MB를 초과할 수 없습니다.");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      productImgs: files,
    }));
  };

  const handleDescImageInsert = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert("이미지 파일 크기는 10MB를 초과할 수 없습니다.");
      return;
    }

    const blobUrl = URL.createObjectURL(file);
    const img = document.createElement("img");
    img.src = blobUrl;
    img.style.maxWidth = "100%";
    img.style.height = "auto";

    // 저장된 커서 위치 복원 (버튼 클릭 시 저장)
    const range =
      savedRangeRef.current ||
      (() => {
        const r = document.createRange();
        r.selectNodeContents(descEditorRef.current);
        r.collapse(false);
        return r;
      })();
    savedRangeRef.current = null;

    descEditorRef.current.focus();
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    range.deleteContents();
    range.insertNode(img);
    range.setStartAfter(img);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    setDescImgFiles((prev) => [...prev, { blobUrl, file }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.categoryIdx) {
      alert("카테고리를 선택해주세요.");
      return;
    }

    if (!formData.productName.trim()) {
      alert("상품명을 입력해주세요.");
      return;
    }

    if (!formData.productPrice) {
      alert("상품가격을 입력해주세요.");
      return;
    }

    if (formData.isStock && !formData.productAmount) {
      alert("재고량을 입력해주세요.");
      return;
    }

    if (!formData.productImgs || formData.productImgs.length === 0) {
      alert("상품 이미지를 등록해주세요.");
      return;
    }

    setLoading(true);

    try {
      const formDataObj = new FormData();
      formDataObj.append("categoryIdx", formData.categoryIdx);
      formDataObj.append("productName", formData.productName);
      formDataObj.append("productPrice", parseInt(formData.productPrice));
      formDataObj.append("isAdult", false);
      formDataObj.append("isStock", formData.isStock);
      formDataObj.append(
        "productAmount",
        formData.productAmount ? parseInt(formData.productAmount) : 0,
      );
      // blob URL을 [IMG_X] 플레이스홀더로 치환
      let descHtml = descEditorRef.current?.innerHTML || "";
      descImgFiles.forEach(({ blobUrl }, idx) => {
        descHtml = descHtml.split(blobUrl).join(`__PENDING_IMG_${idx}__`);
      });
      formDataObj.append("productDetailDescription", descHtml);
      descImgFiles.forEach(({ file }) => {
        formDataObj.append("productDescriptionImgs", file);
      });

      formData.productImgs.forEach((file) => {
        formDataObj.append("productImgs", file);
      });

      const response = await apiPostFormData("/store/product", formDataObj);

      if (response.ok) {
        alert("상품이 등록되었습니다.");
        onClose();
      } else {
        const errorData = await response.json();
        alert(errorData.message || "상품 등록에 실패했습니다.");
      }
    } catch (err) {
      console.error("등록 실패:", err);
      alert("상품 등록 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="productadd-wrap">
      <div className="productadd-modal">
        <div className="productadd-header">상품등록</div>

        <form onSubmit={handleSubmit}>
          <div className="productadd-info">
            <div className="productadd-title">기본 정보</div>
            <div className="productadd-info-content-head">
              <div className="productadd-content-title">카테고리</div>
              <div className="productadd-content-body">
                <select
                  name="categoryIdx"
                  value={formData.categoryIdx}
                  onChange={handleChange}
                  className="productadd-input-category"
                >
                  <option value="">카테고리 선택</option>
                  {categories.map((cat) => (
                    <option key={cat.categoryIdx} value={cat.categoryIdx}>
                      {cat.categoryNm}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="productadd-info-content-body">
              <div className="productadd-content-title">상품명</div>
              <div className="productadd-content-body">
                <input
                  type="text"
                  name="productName"
                  value={formData.productName}
                  onChange={handleChange}
                  className="productadd-input"
                  placeholder="상품명 입력"
                />
              </div>
            </div>
            <div className="productadd-info-content-footer">
              <div className="productadd-content-title">상품가격</div>
              <div className="productadd-content-body">
                <input
                  type="number"
                  name="productPrice"
                  value={formData.productPrice}
                  onChange={handleChange}
                  className="productadd-input"
                  placeholder="상품가격 입력"
                  maxLength={10}
                />
              </div>
            </div>
          </div>

          {/* <div className="productadd-expose">
            <div className="productadd-title">노출 설정</div>
            <div className="productadd-expose-content">
              <div className="productadd-expose-content-body">
                <div className="productadd-content-title">상품분류</div>
                <div className="productadd-content-body">
                  성인
                  <input
                    type="radio"
                    name="isAdult"
                    value="true"
                    checked={formData.isAdult === true}
                    onChange={(e) => setFormData(prev => ({ ...prev, isAdult: true }))}
                    className="productadd-radio"
                  />{" "}
                  일반
                  <input
                    type="radio"
                    name="isAdult"
                    value="false"
                    checked={formData.isAdult === false}
                    onChange={(e) => setFormData(prev => ({ ...prev, isAdult: false }))}
                    className="productadd-radio"
                  />
                </div>
              </div>
            </div>
          </div> */}

          <div className="productadd-stock-info">
            <div className="productadd-title">재고 정보</div>
            <div className="productadd-stock-info-content">
              <div className="productadd-stock-info-content-head">
                <div className="productadd-content-title">상품 구분</div>
                <div className="productadd-content-body">
                  일반
                  <input
                    type="radio"
                    name="isStock"
                    value="false"
                    checked={formData.isStock === false}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, isStock: false }))
                    }
                    className="productadd-radio"
                  />
                  서비스
                  <input
                    type="radio"
                    name="isStock"
                    value="true"
                    checked={formData.isStock === true}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, isStock: true }))
                    }
                    className="productadd-radio"
                  />{" "}
                </div>
              </div>
              <div className="productadd-stock-info-content-body">
                <div className="productadd-content-title">재고량</div>
                <div className="productadd-content-body">
                  <input
                    type="number"
                    name="productAmount"
                    value={formData.productAmount}
                    onChange={handleChange}
                    className="productadd-input"
                    placeholder="수량 입력"
                    maxLength={10}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="productadd-detail-info">
            <div className="productadd-title">상세 정보</div>
            <div className="productadd-detail-info-content">
              <div className="productadd-detail-info-content-head">
                <div className="productadd-content-title-text">상품 설명</div>
                <div className="productadd-content-body-text">
                  <div className="desc-editor-wrap">
                    <div className="desc-editor-toolbar">
                      <button
                        type="button"
                        className="desc-insert-image-btn"
                        onClick={() => {
                          const sel = window.getSelection();
                          if (
                            sel.rangeCount > 0 &&
                            descEditorRef.current.contains(sel.anchorNode)
                          ) {
                            savedRangeRef.current = sel
                              .getRangeAt(0)
                              .cloneRange();
                          }
                          descImgInputRef.current?.click();
                        }}
                      >
                        📷 이미지 삽입
                      </button>
                      <input
                        ref={descImgInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        style={{ display: "none" }}
                        onChange={handleDescImageInsert}
                      />
                    </div>
                    <div
                      ref={descEditorRef}
                      contentEditable={true}
                      className="desc-editor-content"
                      suppressContentEditableWarning={true}
                      data-placeholder="상품 설명 입력"
                    />
                  </div>
                </div>
              </div>
              <div className="productadd-detail-info-content-body">
                <div className="productadd-content-title">첨부파일</div>
                <div className="productadd-content-body">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="productadd-file"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="productadd-btn-group">
            <button
              type="submit"
              className="productadd-reg-btn"
              disabled={loading}
            >
              {loading ? "등록 중..." : "등록"}
            </button>
            <button
              type="button"
              className="productadd-close-btn"
              onClick={onClose}
              disabled={loading}
            >
              닫기
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductAdd;
